/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import Funil from "@/assets/icons/funil";
import { Checkbox } from "components/ui/checkbox";
import { useEffect } from "react";
import {
  Controller,
  FieldValues,
  FormProvider,
  UseFormReturn,
} from "react-hook-form";

import { formatToBRL, formatToMillionBRL } from "@/utils/formatToMoney";
import { Button } from "components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/ui/tooltip";
import { MoreVerticalIcon } from "lucide-react";
import ActionCellTable from "../components/action-cell-table";
import { CommentCell } from "../components/comment-cell";
import { JustiticationDatePicker } from "../components/justification-date-picker";
import SelectCellJustificationWithSearch from "../components/select-cell-justification/with-search";
import { SimpleConciliatedDivergencesPopover } from "../components/simple-conciliated-popover";
import { useDivergenceFilterActions } from "../ducks/filter/hooks/actions";
import { useServerFieldSync } from "../hooks/use-server-field-sync";
import { ServerFieldSync } from "../types";

interface IUseColumnsProps {
  control: any;
  methods: UseFormReturn<FieldValues, any, undefined>;
  totalAmount: number;
  isClosed: boolean;
}

export const useColumns = ({
  methods,
  totalAmount,
  isClosed,
}: IUseColumnsProps) => {
  const { control, setValue, watch } = methods;
  const { updateFilter } = useDivergenceFilterActions();
  const syncFromServer: ServerFieldSync = useServerFieldSync(methods);

  return [
    {
      accessorKey: "Operador",
      header: () => (
        <>
          <div className="flex max-w-14 justify-between items-center">
            <span>Operador</span>
          </div>
        </>
      ),
      cell: ({ row }: { row: any }) => {
        useEffect(() => {
          setValue(`divergence.id_${row.original.id}`, row.original.id);
        }, [row.original.id]);

        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="min-w-[20px] max-h-12 overflow-hidden">
                  <p className="line-clamp-2">{row.original.operator}</p>
                </div>
              </TooltipTrigger>
              <TooltipContent className="bg-[#141414] text-white text-center">
                <p>{row.original.operator}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
    },
    {
      accessorKey: "Caixa",
      header: () => (
        <>
          <div className="flex w-full justify-between items-center">
            <span>Caixa</span>
          </div>
        </>
      ),
      cell: ({ row }: { row: any }) => {
        setValue(`divergence.id_${row.original.id}`, row.original.id);

        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="max-h-12 overflow-hidden">
                  <p className="line-clamp-2">{row.original.register_code}</p>
                </div>
              </TooltipTrigger>
              <TooltipContent className="bg-[#141414] text-white text-center">
                <p>{row.original.register_code}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
    },
    {
      id: "divergence",
      header: () => (
        <>
          <div
            className="flex max-w-24 justify-between items-center"
            onClick={() => {
              updateFilter({
                key: "popoverFilterDivergence",
                value: true,
              });
            }}
          >
            <span>Divergência</span>
            <Funil color="black" size={16} />
          </div>
        </>
      ),
      cell: ({ row }: { row: any }) => {
        setValue(`divergence.id_${row.original.id}`, row.original.id);
        setValue(`divergence.version_${row.original.id}`, row.original.version);

        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="min-w-[80px] max-h-12 overflow-hidden">
                  <p className="line-clamp-2">{row.original.name}</p>
                </div>
              </TooltipTrigger>
              <TooltipContent className="bg-[#141414] text-white text-center">
                <p>{row.original.name}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
    },
    {
      accessorKey: "Valor",
      header: () => (
        <div className="flex gap-4 w-full items-center justify-start">
          Valor
        </div>
      ),
      cell: ({ row }: { row: any }) => (
        <div className="w-full flex items-center justify-start min-w-[60px] md:min-w-[100px] lg:min-w-[70px]">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <p className="cursor-pointer whitespace-nowrap">
                  {formatToMillionBRL(+row?.original?.amount)}
                </p>
              </TooltipTrigger>
              <TooltipContent className="bg-[#141414] text-white text-center">
                <p>{formatToBRL(+row?.original?.amount)}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      ),
    },
    {
      id: "Justificativa",
      header: () => (
        <div
          className="flex w-full items-center"
          onClick={() => {
            updateFilter({
              key: "popoverFilterJustification",
              value: true,
            });
          }}
        >
          <span>Justificativa</span>
          <Funil color="black" size={16} />
        </div>
      ),
      cell: ({ row }: { row: any }) => {
        const justificationId = row.original.justification_id || (Math.abs(totalAmount) <= 5 ? 26 : undefined);

        return (
          <div
            className={`full p-0 ${isClosed
              ? "opacity-50 pointer-events-none cursor-not-allowed"
              : ""
              }`}
          >
            <FormProvider {...methods}>
              <form>
                <SelectCellJustificationWithSearch
                  key={`justification-${row.original.id}-${row.original.version || 0}-${justificationId}`}
                  control={control}
                  justification={justificationId ?? ""}
                  rowId={row.original.id}
                />
              </form>
            </FormProvider>
          </div>
        );
      },
    },
    {
      id: "justification_date",
      header: () => (
        <div className="flex items-center justify-start w-max">
          Data da Justificativa
        </div>
      ),
      cell: ({ row }: { row: any }) => {
        const currentJustificationId = watch(
          `divergence.justification_${row.original.id}`
        );
        const conciliationDate = row.original.conciliation.date;

        return (
          <div className="w-full flex flex-col items-center justify-start p-0">
            <JustiticationDatePicker
              row={row}
              setValue={setValue}
              justificationId={+currentJustificationId}
              conciliationDate={conciliationDate}
              isClosed={isClosed}
            />
          </div>
        );
      },
      className: "w-[200px] items-center justify-center !p-0",
    },
    {
      id: "conciliation_info",
      header: () => (
        <div className="flex items-center justify-center w-max">
          Informações de Conciliação
        </div>
      ),
      cell: ({ row }: { row: any }) => {
        const values = row.original;

        // Verificar se existe conciliação
        const hasConciliatedDivergence =
          Array.isArray(values.conciliatedDivergence?.divergences) &&
          values.conciliatedDivergence.divergences.length > 1;
        const conciliatedDivergences =
          hasConciliatedDivergence && Array.isArray(values.conciliatedDivergence.divergences)
            ? values.conciliatedDivergence.divergences.filter((divergence: any) => divergence.id !== row.original.id)
            : [];

        // Verificar se existe derived_from e fazer parse se for string
        let derivedFromData: any[] = [];
        let hasDerivedFrom = false;

        if (values.derived_from) {
          try {
            // Se for string, fazer parse do JSON
            if (typeof values.derived_from === 'string') {
              derivedFromData = JSON.parse(values.derived_from);
            } else if (Array.isArray(values.derived_from)) {
              derivedFromData = values.derived_from;
            }
            hasDerivedFrom = Array.isArray(derivedFromData) && derivedFromData.length > 0;
          } catch (error) {
            console.warn('Error parsing derived_from:', error);
            hasDerivedFrom = false;
            derivedFromData = [];
          }
        }

        const hasAnyInfo = hasConciliatedDivergence || hasDerivedFrom;

        return (
          <div className="w-full flex items-center justify-center p-0">
            {hasAnyInfo ? (
              <SimpleConciliatedDivergencesPopover
                values={values}
                conciliatedDivergences={conciliatedDivergences}
                derivedFromData={derivedFromData}
                hasConciliatedDivergence={hasConciliatedDivergence}
                hasDerivedFrom={hasDerivedFrom}
              />
            ) : (
              <span className="text-gray-400 text-sm">—</span>
            )}
          </div>
        );
      },
      className: "w-[140px] items-center justify-center !p-0",
    },
    {
      accessorKey: "Contabiliza Perda",
      header: () => (
        <div className="flex items-center justify-center w-max">
          Contabiliza perda
        </div>
      ),
      cell: ({ row }: { row: any }) => {
        syncFromServer(`divergence.is_loss_${row.original.id}`, row.original.is_loss);
        return (
          <FormProvider {...methods}>
            <form>
              <div
                className={`w-full flex items-center justify-center ${isClosed
                  ? "opacity-50 pointer-events-none cursor-not-allowed"
                  : ""
                  }`}
              >
                <Controller
                  name={`divergence.is_loss_${row.original.id}`}
                  control={control}
                  defaultValue={row.original.is_loss}
                  render={({ field }) => (
                    <Checkbox
                      id={`divergence.is_loss_${row.original.id}`}
                      checked={field.value || false}
                      onCheckedChange={(checked) => {
                        setValue(
                          `divergence.is_loss_${row.original.id}`,
                          checked
                        );
                      }}
                    />
                  )}
                />
              </div>
            </form>
          </FormProvider>
        );
      },
    },
    {
      accessorKey: "Advertência",
      header: () => (
        <div className="flex items-center justify-center w-max">
          Advertência
        </div>
      ),
      cell: ({ row }: { row: any }) => {
        syncFromServer(
          `divergence.has_penalty_${row.original.id}`,
          row.original.has_penalty || false,
        );
        return (
          <FormProvider {...methods}>
            <form>
              <div
                className={`w-full flex items-center justify-center ${isClosed
                  ? "opacity-50 pointer-events-none cursor-not-allowed"
                  : ""
                  }`}
              >
                <Controller
                  name={`divergence.has_penalty_${row.original.id}`}
                  control={control}
                  defaultValue={row.original.has_penalty || false}
                  render={({ field }) => (
                    <Checkbox
                      id={`divergence.has_penalty_${row.original.id}`}
                      checked={field.value || false}
                      onCheckedChange={(checked) => {
                        setValue(
                          `divergence.has_penalty_${row.original.id}`,
                          checked
                        );
                      }}
                    />
                  )}
                />
              </div>
            </form>
          </FormProvider>
        );
      },
    },
    {
      accessorKey: "Evidências",
      header: () => (
        <div className="flex items-center justify-center">Evidências</div>
      ),
      cell: ({ row }: { row: any }) => {
        syncFromServer(
          `divergence.has_evidence_${row.original.id}`,
          row.original.has_evidence
        );
        return (
          <FormProvider {...methods}>
            <form>
              <div
                className={`w-full flex items-center justify-center ${isClosed
                  ? "opacity-50 pointer-events-none cursor-not-allowed"
                  : ""
                  }`}
              >
                <Controller
                  name={`divergence.has_evidence_${row.original.id}`}
                  control={control}
                  defaultValue={row.original.has_dependency}
                  render={({ field }) => (
                    <Checkbox
                      id={`divergence.has_evidence_${row.original.id}`}
                      checked={field.value || false}
                      onCheckedChange={(checked) => {
                        setValue(
                          `divergence.has_evidence_${row.original.id}`,
                          checked
                        );
                      }}
                    />
                  )}
                />
              </div>
            </form>
          </FormProvider>
        );
      },
    },
    {
      accessorKey: "Comentários",
      header: () => (
        <div className="flex gap-4 w-full items-center justify-center">
          Comentários
        </div>
      ),
      cell: ({ row }: { row: any }) => (
        <CommentCell
          divergenceId={row.original.id}
          divergenceName={row.original.name}
          isClosed={isClosed}
          totalComments={row.original.total_comments ?? 0}
        />
      ),
    },
    {
      id: "actions",
      header: () => (
        <div className="flex items-center justify-center">Ações</div>
      ),
      cell: ({ row }: { row: any }) => (
        <div className="w-full flex items-center justify-center gap-4">
          <ActionCellTable
            row={row?.original}
            trigger={
              <Button
                variant="link"
                className="p-0 m-0 h-[0px] flex items-center justify-center"
              >
                <MoreVerticalIcon className="w-[20px]" />
              </Button>
            }
          />
        </div>
      ),
    },
  ].filter(Boolean);
};

export default useColumns;
